import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError, AuditEntry, api, isAbortError, useCurrentUser } from "../api";
import Spinner from "../components/Spinner";

const ALL = "__all__";

/**
 * Master-only audit log page. Reads /api/admin/audit and renders the most
 * recent entries with light filtering by user and action.
 *
 * The route is gated on the frontend (sidebar link only shows for master)
 * AND on the backend (`require_master` dep), so a regular admin who hand-
 * types the URL gets a 403 from the API and a "you don't have access"
 * message rendered here.
 */
export default function Audit() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<string>(ALL);
  const [actionFilter, setActionFilter] = useState<string>(ALL);

  const isMaster = !!me && me.role === "master";

  // Bounce non-masters (and unauthenticated users) straight to /admin.
  // The backend's require_master is the real gate; this is purely UX —
  // no flash of "access denied" content.
  useEffect(() => {
    if (!isMaster) navigate("/admin", { replace: true });
  }, [isMaster, navigate]);

  useEffect(() => {
    if (!isMaster) return;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    api
      .getAuditLog(
        {
          user: userFilter === ALL ? undefined : userFilter,
          action: actionFilter === ALL ? undefined : actionFilter,
          limit: 200,
        },
        ctrl.signal,
      )
      .then(setEntries)
      .catch((e) => {
        if (isAbortError(e)) return;
        if (e instanceof ApiError && e.status === 401) {
          navigate("/admin");
        } else {
          setError(e instanceof Error ? e.message : "Failed to load");
        }
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [isMaster, userFilter, actionFilter, navigate]);

  // Render nothing while the redirect-to-/admin effect runs. Hooks above
  // already fired in stable order so this conditional return is safe.
  if (!isMaster) return null;

  // Build filter dropdown options from the visible entries — covers most
  // real-world usage without needing a separate /api/admin/audit/users endpoint.
  const usernames = Array.from(
    new Set(entries.map((e) => e.username).filter(Boolean)),
  ) as string[];
  const actions = Array.from(
    new Set(entries.map((e) => e.action).filter(Boolean)),
  ) as string[];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit log</h1>
          <p className="text-sm text-gray-400">
            Master-only. Latest 200 admin actions sorted newest first.
          </p>
        </div>
        <Link
          to="/admin"
          className="text-xs text-gray-400 hover:text-gray-200"
        >
          ← Back to admin
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <FilterSelect
          label="User"
          value={userFilter}
          options={usernames}
          onChange={setUserFilter}
        />
        <FilterSelect
          label="Action"
          value={actionFilter}
          options={actions}
          onChange={setActionFilter}
        />
        <div className="text-gray-500 ml-auto">
          {loading ? "" : `${entries.length} entries`}
        </div>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}
      {loading ? (
        <Spinner label="Loading audit log…" />
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500">No entries.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-bg/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-border">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Slug / Code</th>
                <th className="px-3 py-2 font-medium">Meta</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr
                  key={`${e.at}-${i}`}
                  className="border-t border-border/40 hover:bg-white/5"
                >
                  <td className="px-3 py-1.5 tabular-nums whitespace-nowrap text-gray-400">
                    {formatWhen(e.at)}
                  </td>
                  <td className="px-3 py-1.5 font-medium">{e.username ?? "—"}</td>
                  <td className="px-3 py-1.5">
                    <ActionPill action={e.action} />
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {e.slug ? (
                      <Link
                        to={`/r/${e.slug}`}
                        className="text-accent hover:underline"
                        title={e.slug}
                      >
                        {e.slug.slice(0, 8)}…
                      </Link>
                    ) : e.wclCode ? (
                      <span className="text-gray-400" title={e.wclCode}>
                        {e.wclCode}
                      </span>
                    ) : (
                      <span className="text-gray-700">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-gray-500">
                    {e.meta && Object.keys(e.meta).length > 0 ? (
                      <code className="text-[10px]">{JSON.stringify(e.meta)}</code>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-gray-400">
      <span>{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-panel px-2 py-1 outline-none focus:border-accent"
      >
        <option value={ALL}>all</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function ActionPill({ action }: { action?: string }) {
  if (!action) return <span className="text-gray-700">—</span>;
  // Color-code by intent: green for success, red for failure / destructive.
  const tone =
    action === "login"
      ? "bg-emerald-500/15 text-emerald-300"
      : action === "login_failed"
      ? "bg-red-500/15 text-red-300"
      : action === "import"
      ? "bg-blue-500/15 text-blue-300"
      : action === "reanalyze"
      ? "bg-amber-500/15 text-amber-300"
      : action === "archive"
      ? "bg-gray-500/15 text-gray-300"
      : action === "unarchive"
      ? "bg-gray-500/10 text-gray-400"
      : "bg-gray-500/10 text-gray-400";
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${tone}`}>
      {action}
    </span>
  );
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
